import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import {
  DocketAPIAlreadyEnqueuedError,
  DocketAPIError,
  getAPIClient,
  LegalSex,
  legalSexFromString,
} from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { infoLog } from "../../utils/logger";
import {
  checkBoxValueMap,
  getLocalizedLegalSexChoices,
  getWhoAmIChoices,
  getWhoAmiRadios,
  SearchFormSchema,
  getWhoAmIChildChoices,
  Radios,
  getSearchRadios,
} from "../../utils/formUtils";
import ResponsiveDatePicker from "../../components/form/ResponsiveDatePicker";
import { FormFieldset } from "../../components/form/FormFieldset";
import { DateTime } from "luxon";
import lodash from "lodash";
import { ImmunizationNewSearch } from "../../models/Interfaces";
import { readableDate, getDateTime } from "../../utils/data";
import {
  akSearchFooter,
  idSearchFooter,
  mnSearchFooter,
  njSearchFooter,
  utSearchFooter,
} from "../../components/LegalFooters";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  enqueuedIzSearchAtom,
  izProviderConfigsAtom,
  izSearchAPIResultAtom,
  selectedSearchAtom,
  userAtom,
  isHydrated,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { SUBDOMAIN, WHITELABEL_KEY } from "../../globals";
import i18n from "../../i18n";
import { LegacyEnqueueSearchRequestDto } from "@docket/consumer-app-client-typescript-axios";

export type SearchFormProps = {
  adultChild: "adult" | "child";
  onRunSearch: () => void;
};

export function SearchForm({ adultChild, onRunSearch }: SearchFormProps) {
  const navigate = useNavigate();
  // searches selector to update UI
  const [existingSearch, setExistingSearch] = useAtom(selectedSearchAtom);
  const [submitting, setSubmitting] = useState(false);
  const [user] = useAtom(userAtom);
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const [_, setEnqueuedIzSearch] = useAtom(enqueuedIzSearchAtom);
  const [providerConfigs] = useAtom(izProviderConfigsAtom);
  const { t } = useTranslation();
  // patient or parent DOB
  const [dob, setDob] = useState<Date | null>(
    existingSearch
      ? getDateTime(existingSearch.dateOfBirth).toJSDate()
      : user?.dob
      ? getDateTime(user.dob).toJSDate()
      : DateTime.now().minus({ years: 18 }).toJSDate()
  );
  const idPrefix = useId();

  // Zipcode state
  const [zipcode, setZipcode] = useState<string>(existingSearch?.zipcode || "");
  const [zipcodeError, setZipcodeError] = useState<string>("");
  const [showZipcode, setShowZipcode] = useState<boolean>(false);
  const [zipcodeRequired, setZipcodeRequired] = useState<boolean>(false);

  // child DOB
  const [cDob, setCDob] = useState<Date | null>(
    existingSearch && existingSearch.childDateOfBirth
      ? getDateTime(existingSearch.childDateOfBirth).toJSDate()
      : new Date()
  );
  // DOB checkbox, to map selection to values
  const [legalSexCheckBoxes, setLegalSexCheckBoxes] = useState(getLocalizedLegalSexChoices(t));
  // Child DOB checkbox, to map selection to values
  const [childLegalSexCheckBoxes, setChildLegalSexCheckBoxes] = useState(
    getLocalizedLegalSexChoices(t)
  );
  // who am I checkbox, to map selection to values
  // ToDo: set initial state from prev search or user object
  const [whoAmICheckBoxes, setWhoAmICheckBoxes] = useState(getWhoAmIChoices(t));
  const [gaveConsent, setGaveConsent] = useState(false);
  const [showChildSearch, setShowChildSearch] = useState(
    existingSearch && existingSearch.childFirstName ? true : false
  );
  const [provider, setProvider] = useState("");

  useEffect(() => {
    if (!isHydrated(providerConfigs)) {
      return;
    }

    const supportedSubdomains = providerConfigs.map((config) =>
      config.iz_provider.destination_id.toLowerCase()
    );

    let newProvider = "";

    // Precedence 1: SUBDOMAIN if supported
    if (supportedSubdomains.includes(SUBDOMAIN.toLowerCase())) {
      newProvider = SUBDOMAIN.toLowerCase();
    }

    // Precedence 2: WHITELABEL_KEY === "nj" defaults to "nj"
    if (WHITELABEL_KEY === "nj") {
      newProvider = "nj";
    }

    // Precedence 3: existingSearch takes highest priority
    if (existingSearch?.izProviderKey) {
      newProvider = existingSearch.izProviderKey.toLowerCase();
    }

    setProvider(newProvider);
  }, [providerConfigs, existingSearch]);

  // Effect to determine if zipcode is required based on provider
  useEffect(() => {
    if (!provider || !isHydrated(providerConfigs)) {
      setShowZipcode(false);
      setZipcodeRequired(false);
      return;
    }

    const selectedProvider = providerConfigs.find(
      (config) => config.iz_provider.destination_id.toLowerCase() === provider
    );

    const hasZipcode = selectedProvider?.additional_fields?.zipcode;
    setShowZipcode(!!hasZipcode);
    setZipcodeRequired(
      !!hasZipcode && selectedProvider.additional_fields.zipcode.required === true
    );
  }, [provider, providerConfigs]);

  const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Filter out non-numeric characters and limit to 5 digits
    const filteredValue = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
    setZipcode(filteredValue);

    if (filteredValue.length === 5 || (!zipcodeRequired && filteredValue.length === 0)) {
      setZipcodeError("");
    }
  };

  const validateZipcode = (value: string) => {
    if (zipcodeRequired && (!value || value.trim() === "")) {
      return t("immunizations.search_check_zipcode_required");
    }

    if (value && !/^\d{5}$/.test(value)) {
      if (!zipcodeRequired && value.trim() === "") {
        // Optional zip code and the field is empty
        // If it ISN'T empty, we still want to validate it.
        return "";
      }
      return t("immunizations.search_check_zipcode_format");
    }
    return "";
  };

  const handleZipcodeBlur = () => {
    const error = validateZipcode(zipcode);
    setZipcodeError(error);
  };

  const checkboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(legalSexCheckBoxes, index);
    setLegalSexCheckBoxes(newValue);
  };
  // Child DOB checkbox, to map selection to values
  const childCheckboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(childLegalSexCheckBoxes, index);
    setChildLegalSexCheckBoxes(newValue);
  };
  // who am I checkbox, to map selection to values
  // ToDo: set initial state from prev search or user object
  const whoAmIHandler = (value: any, index: number) => {
    infoLog(`whoAmIHandler ${value} ${index}`);
    const newValue = checkBoxValueMap(whoAmICheckBoxes, index);
    setWhoAmICheckBoxes(newValue);
  };
  const handleConsentChange = (checked: boolean) => {
    setGaveConsent(checked);
  };

  // Handle provider change - reset zipcode when changing providers
  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvider(e.target.value.toLowerCase());
    setZipcode("");
    setZipcodeError("");
  };

  type ErrorModalParams = {
    title: string;
    body: string;
    show: boolean;
  };
  const [showErrorModal, setShowErrorModal] = useState<ErrorModalParams>({
    title: "",
    body: "",
    show: false,
  });
  const [title, message] = t("immunizations.search_check_search_in_queue").split("|");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(SearchFormSchema),
  });

  const searchFormTranslations = {
    firstName: t("immunizations.search_my_first_name_label"),
    lastName: t("immunizations.search_my_last_name_label"),
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
    zipCode: t("immunizations.search_zip_code"),
    childFirstName: t("immunizations.search_child_first_name_label"),
    childLastName: t("immunizations.search_child_last_name_label"),
    childDateOfBirth: t("immunizations.search_child_dob_label"),
    childLegalSex: t("immunizations.search_child_legal_sex_label"),
    consentTitle: t("immunizations.search_consent_title"),
    consentMessage: t("immunizations.search_consent_message"),
    consentCheckBoxLabel: t("immunizations.i_consent"),
  };

  useEffect(() => {
    if (existingSearch?.dateOfBirth) {
      setDob(getDateTime(existingSearch.dateOfBirth).toJSDate());
    } else if (user?.dob) {
      setDob(getDateTime(user.dob).toJSDate());
    }
    return () => {};
  }, [existingSearch, user]);

  useEffect(() => {
    if (existingSearch?.zipcode) {
      setZipcode(existingSearch.zipcode);
    }
    return () => {};
  }, [existingSearch]);

  useEffect(() => {
    const userLegalSex = user?.legal_sex;
    const value = checkBoxRenderHelper(
      legalSexCheckBoxes,
      existingSearch?.legalSex,
      userLegalSex ? legalSexFromString(userLegalSex) : undefined
    );
    setLegalSexCheckBoxes(value);
  }, [existingSearch, user]);

  useEffect(() => {
    setChildLegalSexCheckBoxes(
      checkBoxRenderHelper(childLegalSexCheckBoxes, existingSearch?.childLegalSex)
    );
  }, [existingSearch]);

  useEffect(() => {
    setWhoAmICheckBoxes(checkBoxRenderHelper(whoAmICheckBoxes, existingSearch?.whoAmI));
  }, [existingSearch, user]);

  useEffect(() => {
    // reset was the only way to get hook form to see the form values as valid
    reset({
      firstName: existingSearch?.firstName ?? user?.first_name ?? "",
      lastName: existingSearch?.lastName ?? user?.last_name ?? "",
      childFirstName: existingSearch?.childFirstName ?? "",
      childLastName: existingSearch?.childLastName ?? "",
    });
  }, [existingSearch, user]);

  const onSubmit = async (data: any) => {
    infoLog(`onSubmit ${JSON.stringify(data)}`);
    setSubmitting(true);
    try {
      // This will control the error modal as well, so we don't need to worry about it here.
      const dobsValid = await checkDates();
      //let checkDup = await checkForDuplicateSearch(data)
      const sex = lodash.find(legalSexCheckBoxes, ["checked", true]);
      const csex = lodash.find(childLegalSexCheckBoxes, ["checked", true]);
      const who = lodash.find(whoAmICheckBoxes, ["checked", true]);
      const legalSex = sex?.value ?? null;
      const cLegalSex = csex?.value ?? null;

      // Validate child search data first since it appears at the top of the form
      if (adultChild === "child") {
        let errorMsg = "";
        if (!data.childFirstName) {
          errorMsg = t("immunizations.search_check_child_first_name");
        } else if (!data.childLastName) {
          errorMsg = t("immunizations.search_check_child_last_name");
        } else if (!cLegalSex) {
          errorMsg = t("immunizations.search_check_child_legal_sex");
        } else if (!cDob) {
          errorMsg = t("immunizations.search_check_child_dob");
        }

        if (errorMsg.length > 0) {
          setShowErrorModal({
            title: t("immunizations.search_check_header"),
            body: errorMsg,
            show: true,
          });
          return;
        }
      }

      // Add zipcode validation
      if (showZipcode) {
        const zipcodeValidationError = validateZipcode(zipcode);
        if (zipcodeValidationError) {
          setShowErrorModal({
            title: t("immunizations.search_check_header"),
            body: zipcodeValidationError,
            show: true,
          });
          return;
        }
      }

      let errorMsg = "";
      if (!data.firstName) {
        errorMsg = t("immunizations.search_check_first_name");
      } else if (!data.lastName) {
        errorMsg = t("immunizations.search_check_last_name");
      } else if (!legalSex) {
        errorMsg = t("immunizations.search_check_legal_sex");
      } else if (!(adultChild === "child") && !who) {
        // Child searches can only be guardians, so we skip this check since it's auto-selected for the user.
        errorMsg = t("immunizations.search_check_who_am_i");
      } else if (!gaveConsent) {
        errorMsg = t("immunizations.search_check_consent");
      } else if (!dob) {
        errorMsg = t("immunizations.search_check_dob");
      } else if (!provider || provider.length === 0) {
        errorMsg = t("immunizations.search_check_provider");
      }

      if (errorMsg.length > 0) {
        setShowErrorModal({
          title: t("immunizations.search_check_header"),
          body: errorMsg,
          show: true,
        });
        return;
      }

      if (dobsValid) {
        await runSearch(data, adultChild === "child");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const checkDates = async () => {
    let params: ErrorModalParams = {
      title: "",
      body: "",
      show: false,
    };

    // no child searches if the child is over 18...
    if (
      adultChild === "child" &&
      cDob &&
      DateTime.now().minus({ years: 18 }) >= DateTime.fromJSDate(cDob)
    ) {
      params = {
        title: t("immunizations.search_age_error_child_title"),
        body: t("immunizations.search_age_error_child_18"),
        show: true,
      };
    }
    // no search if the adult is under 18...
    else if (dob && DateTime.now().minus({ years: 18 }) <= DateTime.fromJSDate(dob)) {
      params = {
        title: t("immunizations.search_age_error_title"),
        body: t("immunizations.search_age_error_18"),
        show: true,
      };
    }

    if (params.show) {
      setShowErrorModal(params);
    }
    return !params.show;
  };

  const runSearch = async (data: ImmunizationNewSearch, childSearch: boolean) => {
    let sex = lodash.find(legalSexCheckBoxes, ["checked", true]);
    let whoami = !(adultChild === "child")
      ? lodash.find(whoAmICheckBoxes, ["checked", true])
      : { value: "guardian" };
    let childLegalSex =
      adultChild === "child"
        ? lodash.find(childLegalSexCheckBoxes, ["checked", true])
        : { value: "" };
    data.dateOfBirth = readableDate(dob || "");
    data.legalSex = sex ? sex.value : "U";
    data.childLegalSex = childSearch ? childLegalSex!.value : undefined;
    data.childDateOfBirth = childSearch ? readableDate(cDob || "") : undefined;
    data.childFirstName = childSearch
      ? data.childFirstName
        ? data.childFirstName.trim()
        : ""
      : undefined;
    data.childLastName = childSearch
      ? data.childLastName
        ? data.childLastName.trim()
        : ""
      : undefined;
    data.whoAmI = whoami!.value;
    data.izProviderKey = provider;
    data.zipcode = showZipcode ? zipcode : undefined;

    let searchObj: LegacyEnqueueSearchRequestDto & { iz_provider_key: string; zipcode?: string } = {
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      dob: data.dateOfBirth,
      legal_sex: data.legalSex as LegalSex,
      child_first_name: data.childFirstName,
      child_last_name: data.childLastName,
      child_dob: data.childDateOfBirth,
      child_legal_sex: data.childLegalSex as LegalSex,
      who_am_i: data.whoAmI,
      iz_provider_key: data.izProviderKey,
    };

    if (showZipcode && zipcode) {
      searchObj.zipcode = zipcode;
    }

    //setIsSearchRunning(true)
    try {
      const response = await getAPIClient().enqueueIzSearch(searchObj, searchObj.iz_provider_key);
      await setEnqueuedIzSearch(response);
      /**
       * Do not await this since it could potentially take a long time to
       * complete and we want to navigate immediately.
       *
       * Force an immediate refresh so that the new search and loading record
       * appear quickly in the search screen in case the search is initiated
       * while the poller was already running.
       */
      fetchRecordsUntilUpdated(true);
      /**
       * Clear the existing search since we might be creating a new search from
       * a NO_MATCH and we don't want to be taken to the troubleshooting page.
       */
      await setExistingSearch(null);
      onRunSearch();
    } catch (e: unknown) {
      if (e instanceof DocketAPIAlreadyEnqueuedError) {
        setShowErrorModal({
          title: t("immunizations.search_check_search_in_queue").split("|")[0],
          body: t("immunizations.search_check_search_in_queue").split("|")[1],
          show: true,
        });
      } else if (e instanceof DocketAPIError) {
        setShowErrorModal({
          title: t("generic.error"),
          body: e.message,
          show: true,
        });
      }
    }
  };

  const getInputField = (
    yupField: "firstName" | "lastName" | "childFirstName" | "childLastName",
    displayName: string,
    defaultVal: string = "",
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    ref?: React.MutableRefObject<HTMLInputElement | null>
  ) => {
    const { ref: registerRef, ...registerRest } = register(yupField);
    return (
      <div key={yupField}>
        <FormFieldset label={displayName} required>
          <input
            {...registerRest}
            data-testid={`input-field-${yupField}`}
            className="input input-primary w-full"
            type="text"
            placeholder={displayName}
            defaultValue={defaultVal}
            ref={(el) => {
              registerRef(el);
              if (ref) ref.current = el;
            }}
            onKeyDown={onKeyDown}
          />
        </FormFieldset>
      </div>
    );
  };

  const getZipcodeField = () => {
    return (
      <div className="flex-1">
        <FormFieldset
          label={searchFormTranslations.zipCode}
          required={zipcodeRequired}
          className="p-0"
        >
          <input
            data-testid="input-field-zipcode"
            className={`input w-full text-black dark:text-white ${zipcodeError ? "is-danger" : ""}`}
            type="text"
            placeholder={searchFormTranslations.zipCode}
            value={zipcode}
            onChange={handleZipcodeChange}
            onBlur={handleZipcodeBlur}
            maxLength={5}
            inputMode="numeric"
          />
        </FormFieldset>
        {zipcodeError && (
          <p className="help is-danger" data-testid="zipcode-error">
            {zipcodeError}
          </p>
        )}
      </div>
    );
  };

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);

  const handleKeyDownFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => {
        lastNameRef.current?.focus();
      }, 50);
    }
  };

  const handleKeyDownLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      lastNameRef.current?.blur();
    }
  };

  const getParentForm = () => {
    return (
      <>
        {adultChild === "child" && (
          <p className="text-xl text-black dark:text-white">
            {t("immunizations.search_parent_guardian_header")}
          </p>
        )}
        <div className="grid max-md:grid-cols-1 grid-cols-2 gap-2 items-start">
          <div className="md:flex-1">
            {getInputField(
              "firstName",
              searchFormTranslations.firstName,
              existingSearch?.firstName
                ? existingSearch?.firstName
                : user?.first_name
                ? user.first_name
                : "",
              handleKeyDownFirstName,
              firstNameRef
            )}
          </div>
          <div className="md:flex-1">
            {getInputField(
              "lastName",
              searchFormTranslations.lastName,
              existingSearch?.lastName
                ? existingSearch?.lastName
                : user?.last_name
                ? user.last_name
                : "",
              handleKeyDownLastName,
              lastNameRef
            )}
          </div>
          <FormFieldset label={searchFormTranslations.dateOfBirth} required>
            <ResponsiveDatePicker selectedDate={dob} setSelectedDate={(d) => setDob(d)} />
          </FormFieldset>
        </div>

        {showZipcode && (
          <div className="flex w-full">
            {getZipcodeField()}
            <div className="flex-1 max-md:hidden"></div>
            <div className="flex-1 max-md:hidden"></div>
          </div>
        )}

        {getSearchRadios(
          legalSexCheckBoxes,
          searchFormTranslations.legalSex,
          checkboxHandler,
          "",
          `${idPrefix}-gender`,
          t
        )}
      </>
    );
  };

  /**
   * Helper for selecting checkbox values with proper priority:
   * 1. If `override` is defined (e.g., from existingSearch), always use it
   * 2. If a checkbox is already checked, keep it
   * 3. Otherwise, use `fallback` if defined (e.g., from user defaults)
   */
  const checkBoxRenderHelper = (checkboxes: Radios[], override?: string, fallback?: string) => {
    // If override is defined, always use it
    if (override !== undefined) {
      return checkBoxValueMap(
        checkboxes,
        lodash.findIndex(checkboxes, (item) => item.value === override)
      );
    }

    // If something is already checked, keep it
    if (checkboxes.find((box) => box.checked)) {
      return checkboxes;
    }

    // Otherwise use fallback if defined
    if (fallback !== undefined) {
      return checkBoxValueMap(
        checkboxes,
        lodash.findIndex(checkboxes, (item) => item.value === fallback)
      );
    }

    return checkboxes;
  };

  const childFirstNameRef = useRef<HTMLInputElement>(null);
  const childLastNameRef = useRef<HTMLInputElement>(null);

  const handleKeyDownChildFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      childLastNameRef.current?.focus();
    }
  };

  const handleKeyDownChildLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      childLastNameRef.current?.blur();
    }
  };

  const getChildForm = () => {
    return (
      <>
        <div className="grid max-md:grid-cols-1 grid-cols-2 gap-2 items-end">
          <div className="md:flex-1">
            {getInputField(
              "childFirstName",
              searchFormTranslations.childFirstName,
              existingSearch?.childFirstName ? existingSearch?.childFirstName : "",
              handleKeyDownChildFirstName,
              childFirstNameRef
            )}
          </div>
          <div className="md:flex-1">
            {getInputField(
              "childLastName",
              searchFormTranslations.childLastName,
              existingSearch?.childLastName ? existingSearch?.childLastName : "",
              handleKeyDownChildLastName,
              childLastNameRef
            )}
          </div>
          <div className="md:flex-1">
            <FormFieldset label={searchFormTranslations.childDateOfBirth} required>
              <ResponsiveDatePicker selectedDate={cDob} setSelectedDate={(d) => setCDob(d)} />
            </FormFieldset>
          </div>
        </div>
        <div>
          {getSearchRadios(
            childLegalSexCheckBoxes,
            searchFormTranslations.childLegalSex,
            childCheckboxHandler,
            "",
            `${idPrefix}-childGender`,
            t
          )}
          <br />
          <hr />
        </div>
      </>
    );
  };

  const getConsentForm = () => {
    return (
      <div key={"consent"}>
        <p className="text-lg font-bold">{searchFormTranslations.consentTitle}</p>
        <p className="text-sm">{searchFormTranslations.consentMessage}</p>
        <label className="fieldset-label mt-2 text-base-content font-bold">
          <input
            className="checkbox checkbox-sm checkbox-primary"
            data-testid="consent-checkbox"
            type="checkbox"
            name={searchFormTranslations.consentCheckBoxLabel}
            checked={gaveConsent}
            onChange={(e) => handleConsentChange(e.target.checked)}
          />
          {searchFormTranslations.consentCheckBoxLabel}
        </label>
      </div>
    );
  };

  return (
    <div className="pb-2">
      {showErrorModal.show && (
        <ErrorModal
          title={showErrorModal.title}
          onCloseModal={() =>
            setShowErrorModal((cur: ErrorModalParams): ErrorModalParams => {
              return { title: cur.title, body: cur.body, show: false };
            })
          }
        >
          <p>{showErrorModal.body}</p>
        </ErrorModal>
      )}
      <div className="max-md:px-6 max-md:pt-6 pt-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2 md:px-6">
            <FormFieldset label={t("immunizations.search_state_registry_label")} required>
              <div className="select select-primary text-base w-full">
                <select
                  data-testid="jurisdiction-selector"
                  value={provider}
                  onChange={handleProviderChange}
                >
                  <option key="select-provider" value="">
                    {t("immunizations.select_provider")}
                  </option>
                  {isHydrated(providerConfigs) &&
                    providerConfigs
                      .sort((a, b) => (a.iz_provider.name < b.iz_provider.name ? -1 : 1))
                      .map((providerConfig) => (
                        <option
                          key={providerConfig.iz_provider.name}
                          value={providerConfig.iz_provider.destination_id.toLowerCase()}
                        >
                          {i18n.exists(`discover.page_${providerConfig.iz_provider.destination_id}`)
                            ? t(`discover.page_${providerConfig.iz_provider.destination_id}`)
                            : providerConfig.iz_provider.name}
                        </option>
                      ))}
                </select>
              </div>
            </FormFieldset>
            {adultChild === "child" && getChildForm()}
            {getParentForm()}
            {adultChild === "child"
              ? getWhoAmiRadios(
                  getWhoAmIChildChoices(t),
                  t("immunizations.i_am"),
                  whoAmIHandler,
                  idPrefix,
                  t
                )
              : getWhoAmiRadios(
                  whoAmICheckBoxes,
                  t("immunizations.i_am"),
                  whoAmIHandler,
                  idPrefix,
                  t
                )}
            <hr />
            {getConsentForm()}

            <button
              type="submit"
              data-testid="search-form-button"
              className="btn btn-lg btn-primary mt-2"
              disabled={submitting}
            >
              {t("immunizations.search_records_button")}
            </button>

            <br />
            <div className="">
              {provider === "ak" && akSearchFooter(t)}
              {provider === "id" && idSearchFooter(t)}
              {provider === "mn" && mnSearchFooter(t)}
              {provider === "nj" && njSearchFooter(t)}
              {provider === "ut" && utSearchFooter(t)}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SearchForm;
